/**
 * Framework tier vocabulary + gating logic. This is the JS mirror of the backend
 * pkg/entitlement/tiers.go. The SKU string is the single join key across Stripe
 * product metadata.tier, framework.framework_availability, and org entitlements.
 *
 * The canonical, sellable option list is Stripe-derived (GET /entitlement/catalog);
 * AVAILABILITY_OPTIONS below is the offline/dev fallback and the base list the
 * admin FrameworkModal falls back to when the catalog fetch fails.
 */

export const ESSENTIALS_TIER = 'Essentials';

export const AVAILABILITY_OPTIONS = [
  'Essentials',
  'Advance',
  'Premium',
  'Cyber Security - Advance',
  'Cyber Security - Premium',
  'Data Protection - Advance',
  'Data Protection - Premium',
  'IP - Advance',
  'IP - Premium',
  'FinTech - Advance',
  'FinTech - Premium',
  'Startup and Investor - Advance',
  'Startup and Investor - Premium',
  'Corporate Finance - Advance',
  'Corporate Finance - Premium',
  'K-12 School - Advance',
  'K-12 School - Premium',
  'Healthcare - Advance',
  'Healthcare - Premium',
  'AI - Advance',
  'AI - Premium',
  'Sustainability - Advance',
  'Sustainability - Premium',
];

/** PAID_TIERS - sellable/grantable SKUs only (Essentials is free, never granted). */
export const PAID_TIERS = AVAILABILITY_OPTIONS.filter((t) => t !== ESSENTIALS_TIER);

/** parseJsonArray - tolerant parse of a JSONB array field (array | JSON string). */
export const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * expandTiers - map purchased SKUs to the full set they grant. Inheritance is
 * downward WITHIN A TRACK ONLY: "X - Premium" also grants "X - Advance"; generic
 * "Premium" also grants "Advance". No cross-track / generic<->industry leak.
 */
export const expandTiers = (purchased = []) => {
  const set = new Set();
  (purchased || []).forEach((raw) => {
    const sku = String(raw || '').trim();
    if (!sku) return;
    set.add(sku);
    if (sku === 'Premium') {
      set.add('Advance');
    } else if (sku.endsWith(' - Premium')) {
      set.add(sku.replace(/ - Premium$/, ' - Advance'));
    }
  });
  return [...set];
};

/**
 * withInheritedGrantTiers - for admin multi-selects: selecting Premium also
 * selects the matching Advance (same inheritance as expandTiers). Essentials is
 * not a grantable SKU — platform access is set on redeem via the org window.
 */
export const withInheritedGrantTiers = (selected = []) => expandTiers(selected);

/**
 * normalizeAvailabilityTags - tolerant parse + trim of a raw availability
 * value, which may be a JSONB array, a JSON string, a Postgres text[]
 * (already a plain array), or null/undefined. Shared core so Framework
 * (`framework_availability`), Template Pack (`framework_availability`), and
 * Single Policy Template (`availability`) all normalize identically.
 */
export const normalizeAvailabilityTags = (tags) =>
  parseJsonArray(tags).map((t) => String(t).trim()).filter(Boolean);

/**
 * isAvailabilityLocked - true when a raw list of availability SKU tags needs
 * a tier the org doesn't hold. Empty availability or an Essentials tag is
 * always free (never locked). This is the generic core reused by every
 * gated entity (Framework, Template Pack, Single Policy Template) so the
 * lock rule can never drift independently between them.
 */
export const isAvailabilityLocked = (tags, effectiveTiers = []) => {
  const availability = normalizeAvailabilityTags(tags);
  if (availability.length === 0 || availability.includes(ESSENTIALS_TIER)) return false;
  const eff = new Set(effectiveTiers || []);
  return !availability.some((sku) => eff.has(sku));
};

/** requiredTiersForTags - the paid SKUs a raw availability tag list needs (minus Essentials). */
export const requiredTiersForTags = (tags) =>
  normalizeAvailabilityTags(tags).filter((t) => t !== ESSENTIALS_TIER);

/** frameworkAvailability - the availability SKU tags on a framework. */
export const frameworkAvailability = (framework) =>
  normalizeAvailabilityTags(framework?.framework_availability);

/** requiredTiersFor - the paid SKUs a framework needs (availability minus Essentials). */
export const requiredTiersFor = (framework) => requiredTiersForTags(framework?.framework_availability);

/**
 * isFrameworkLocked - true when a framework needs a tier the org doesn't hold.
 * Empty availability or an Essentials tag is always free (never locked).
 * Thin wrapper around isAvailabilityLocked — behavior unchanged.
 */
export const isFrameworkLocked = (framework, effectiveTiers = []) =>
  isAvailabilityLocked(framework?.framework_availability, effectiveTiers);

/** hasEssentialsTier - kept for callers that only care about the free tier. */
export const hasEssentialsTier = (framework) =>
  frameworkAvailability(framework).includes(ESSENTIALS_TIER);

/**
 * Display-name overrides for tracks. The Stripe SKUs / metadata.tier strings
 * are the join key and MUST stay as-is (e.g. "IP - Premium"); these overrides
 * only change what the user sees.
 */
const TRACK_DISPLAY_NAMES = { IP: 'Brands & IP' };

/** trackDisplayName - user-facing name for a track ("IP" -> "Brands & IP"). */
export const trackDisplayName = (track) => TRACK_DISPLAY_NAMES[String(track || '').trim()] || track;

/** tierDisplayName - user-facing name for a SKU ("IP - Premium" -> "Brands & IP - Premium"). */
export const tierDisplayName = (sku) => {
  const s = String(sku || '').trim();
  const i = s.lastIndexOf(' - ');
  if (i >= 0) return `${trackDisplayName(s.slice(0, i))} - ${s.slice(i + 3)}`;
  return trackDisplayName(s);
};

/**
 * getFrameworkTierBarContent - label + variant for the framework card tier top bar.
 * Returns null when there is nothing to show (paid framework with no tier name).
 */
export const getFrameworkTierBarContent = (isFree, locked, tierName) => {
  if (isFree) return { label: 'Included in your plan', variant: 'included' };
  const tier = tierDisplayName(String(tierName || '').trim());
  if (!tier) return null;
  if (locked) return { label: `Upgrade to ${tier} to unlock`, variant: 'locked' };
  return { label: tier, variant: 'unlocked' };
};

/**
 * computeAccessWindow - mirror of the backend login gate (login_repo.go): an org
 * can log in when is_paid_client is true OR the trial window still covers now.
 * Window = COALESCE(current_subscription_start_date, created_at) + account_expiration_day.
 * Returns { activeUntil: Date|null, isActive: bool, isPaidOverride: bool }.
 */
export const computeAccessWindow = (org) => {
  const anchor = org?.current_subscription_start_date || org?.created_at;
  const days = Number(org?.account_expiration_day ?? 0);
  const anchorDate = anchor ? new Date(anchor) : null;
  const activeUntil =
    anchorDate && !Number.isNaN(anchorDate.getTime())
      ? new Date(anchorDate.getTime() + days * 864e5)
      : null;
  const isPaidOverride = !!org?.is_paid_client;
  const windowActive = activeUntil ? activeUntil >= new Date() : false;
  return { activeUntil, isPaidOverride, isActive: isPaidOverride || windowActive };
};

/** countryToCurrency - pick the Stripe presentment currency for an org's country. */
export const countryToCurrency = (country) => {
  const c = String(country || '').trim().toLowerCase();
  if (c === 'australia' || c === 'au') return 'aud';
  return 'sgd'; // Singapore default
};

/** Billing period keys for UpgradeModal plan picker. */
export const BILLING_PERIODS = [
  { key: 'monthly', label: 'Monthly', interval: 'month', intervalCount: 1 },
  { key: 'quarterly', label: 'Quarterly', interval: 'month', intervalCount: 3, savingsLabel: 'Save 15%' },
  { key: 'annual', label: 'Annual', interval: 'year', intervalCount: 1, savingsLabel: 'Save 20%' },
];

const priceIntervalCount = (price) => {
  const raw = price?.interval_count ?? price?.intervalCount;
  if (raw === undefined || raw === null || raw === 0) return 1;
  return Number(raw);
};

const priceMatchesPeriod = (price, periodKey) => {
  const cfg = BILLING_PERIODS.find((p) => p.key === periodKey);
  if (!cfg) return false;
  const interval = String(price?.interval || '').toLowerCase();
  return interval === cfg.interval && priceIntervalCount(price) === cfg.intervalCount;
};

/** billingPeriodAvailable - true if any price matches currency + billing period. */
export const billingPeriodAvailable = (prices = [], currency = 'sgd', periodKey = 'monthly') => {
  if (!Array.isArray(prices) || prices.length === 0) return false;
  const byCurrency = prices.filter((p) => String(p.currency || '').toLowerCase() === currency);
  const pool = byCurrency.length > 0 ? byCurrency : prices;
  return pool.some((p) => priceMatchesPeriod(p, periodKey));
};

/** pickPriceForPeriod - choose price for org currency and billing period (monthly/quarterly/annual). */
export const pickPriceForPeriod = (prices = [], currency = 'sgd', periodKey = 'monthly') => {
  if (!Array.isArray(prices) || prices.length === 0) return null;
  const byCurrency = prices.filter((p) => String(p.currency || '').toLowerCase() === currency);
  const pool = byCurrency.length > 0 ? byCurrency : prices;
  const match = pool.find((p) => priceMatchesPeriod(p, periodKey));
  if (match) return match;
  if (periodKey !== 'monthly') return pickPriceForPeriod(prices, currency, 'monthly');
  return pool[0] || null;
};

/** pickPriceForCurrency - choose the monthly price object matching the org currency. */
export const pickPriceForCurrency = (prices = [], currency = 'sgd') =>
  pickPriceForPeriod(prices, currency, 'monthly');

/** periodDisplayLabel - human label for price interval in UI. */
export const periodDisplayLabel = (periodKey) => {
  if (periodKey === 'quarterly') return 'per quarter';
  if (periodKey === 'annual') return 'per year';
  return 'per month';
};

/**
 * frameworksUnlockedByTiers - paid frameworks an org can access when holding grantedTiers.
 * Excludes Essentials-only / untagged free frameworks.
 */
export const frameworksUnlockedByTiers = (frameworks = [], grantedTiers = []) => {
  const eff = new Set(grantedTiers || []);
  return (frameworks || [])
    .filter((fw) => {
      const availability = frameworkAvailability(fw);
      if (availability.length === 0 || availability.includes(ESSENTIALS_TIER)) return false;
      return availability.some((sku) => eff.has(sku));
    })
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
};

/**
 * previousTierSku - the lower rung a Premium SKU inherits from, or null for
 * Advance / Essentials / unknown. Used to compute incremental unlock lists.
 */
export const previousTierSku = (sku) => {
  const s = String(sku || '').trim();
  if (s === 'Premium') return 'Advance';
  if (s.endsWith(' - Premium')) return s.replace(/ - Premium$/, ' - Advance');
  return null;
};

/**
 * frameworksAddedBySku - paid frameworks this SKU newly unlocks vs the prior
 * rung (Premium vs Advance). Advance returns the same as frameworksUnlockedByTiers
 * for that SKU alone (exclusive vs Essentials).
 */
export const frameworksAddedBySku = (frameworks = [], sku) => {
  const full = frameworksUnlockedByTiers(frameworks, expandTiers([sku]));
  const prev = previousTierSku(sku);
  if (!prev) return full;
  const prior = new Set(
    frameworksUnlockedByTiers(frameworks, expandTiers([prev])).map((fw) => fw.id || fw.name)
  );
  return full.filter((fw) => !prior.has(fw.id || fw.name));
};
